import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/forgot-password`, { email });
      setSent(true);
    } catch {
      setError('Ocurrió un error. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png"
                alt="LivCom"
                className="h-10 w-10"
              />
              <span className="text-3xl font-heading font-bold text-primary">LivCom</span>
            </div>
          </div>

          {sent ? (
            /* Success State */
            <div className="text-center animate-fade-in" data-testid="success-message">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-heading font-semibold text-slate-900 mb-2">
                Revisa tu correo
              </h1>
              <p className="text-slate-500 mb-6">
                Te enviamos las instrucciones para restablecer tu contraseña a <strong>{email}</strong>.
              </p>
              <p className="text-sm text-slate-400 mb-8">
                ¿No lo ves? Revisa tu carpeta de spam.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-accent font-medium hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver al login
              </Link>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="mb-8 text-center">
                <div className="h-14 w-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-accent" />
                </div>
                <h1 className="text-2xl font-heading font-semibold text-slate-900 mb-2">
                  ¿Olvidaste tu contraseña?
                </h1>
                <p className="text-slate-500">
                  Ingresa tu email y te enviaremos un enlace para restablecerla.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="mt-1 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg" data-testid="error-message">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-accent hover:bg-accent/90 text-white rounded-full py-6 font-semibold shadow-lg"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Enviando...' : 'Enviar instrucciones'}
                </Button>
              </form>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-text-secondary mt-6 hover:text-primary transition-colors"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver al login
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Right - Decorative */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-primary to-primary/90 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Tu seguridad, primero
            </h2>
            <p className="text-xl text-white/80">
              Enviamos un enlace seguro a tu correo. Expira en 15 minutos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
